import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Check, CheckCircle, Copy, HelpCircle, Loader2, RefreshCw, Radar, XCircle } from 'lucide-react';
import { platformAPI } from '../../services/platformApi';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const errorText = (e) =>
  e?.response?.data?.error || e?.response?.data?.errors?.map((x) => `${x.path}: ${x.msg}`).join(', ') || e?.message;

/** The same four-way dot the status panels use, with `unknown` as a question mark. */
function Dot({ status }) {
  if (status === 'ok') return <CheckCircle className="h-4 w-4 shrink-0 text-green-600" />;
  if (status === 'warn') return <AlertCircle className="h-4 w-4 shrink-0 text-amber-500" />;
  if (status === 'missing') return <XCircle className="h-4 w-4 shrink-0 text-red-500" />;
  return <HelpCircle className="h-4 w-4 shrink-0 text-neutral-400" />;
}

const STATUS_STYLES = {
  ok: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  warn: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  missing: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  unknown: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300',
};

const STATUS_LABELS = { ok: 'Done', warn: 'Attention', missing: 'Missing', unknown: 'Unknown' };

/**
 * A multi-line command block with a copy button. `CopyField` is a one-line
 * truncating field for URLs; most of these actions are several commands and
 * a comment, which have to be read in full.
 */
function CopyBlock({ value }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="relative">
      <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded-lg bg-neutral-900 px-3 py-2 pr-10 font-mono text-xs leading-5 text-neutral-100 dark:bg-neutral-950">
        {value}
      </pre>
      <button
        type="button"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          } catch {
            /* clipboard unavailable */
          }
        }}
        className="absolute right-2 top-2 rounded p-1 text-neutral-400 hover:bg-neutral-800 hover:text-white"
        title="Copy"
      >
        {copied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
}

/**
 * The platform's own one-time setup as a live checklist: what is still
 * missing before a second tenant can be onboarded, and exactly what to do
 * about it. The default load reads env and the registry only; "Run live
 * checks" is the one thing that makes the server ask Cloudflare and DNS.
 */
export default function PlatformSetupPage() {
  const [live, setLive] = useState(false);
  const setup = useQuery({
    queryKey: ['platform-setup', live],
    queryFn: () => platformAPI.platformSetup({ live }),
    select: (r) => r.data,
    // A live run is an explicit click, not something to repeat in the
    // background against a vendor's API.
    refetchOnWindowFocus: !live,
  });

  const items = setup.data?.items || [];
  const counts = items.reduce((acc, i) => ({ ...acc, [i.status]: (acc[i.status] || 0) + 1 }), {});
  const open = items.filter((i) => i.status !== 'ok').length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-neutral-900 dark:text-white">Platform setup</h1>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            What is still missing before a second tenant can be onboarded, and what to do about it. Decided from the
            environment and the registry; nothing reaches Cloudflare or DNS unless you run the live checks.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => (live ? setup.refetch() : setLive(true))}
            disabled={setup.isFetching}
            className="inline-flex items-center gap-1 rounded-lg border border-neutral-300 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-50 disabled:opacity-50 dark:border-neutral-600 dark:text-neutral-200 dark:hover:bg-neutral-700"
            title="Also ask Cloudflare for the fallback origin and probe the API wildcard in DNS"
          >
            {setup.isFetching && live ? <Loader2 className="h-4 w-4 animate-spin" /> : <Radar className="h-4 w-4" />}
            Run live checks
          </button>
          <button
            type="button"
            onClick={() => (live ? setLive(false) : setup.refetch())}
            disabled={setup.isFetching}
            className="inline-flex items-center gap-1 rounded-lg border border-neutral-300 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-50 disabled:opacity-50 dark:border-neutral-600 dark:text-neutral-200 dark:hover:bg-neutral-700"
          >
            {setup.isFetching && !live ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Refresh
          </button>
        </div>
      </div>

      {setup.error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
          {errorText(setup.error)}
        </div>
      )}

      {setup.isLoading && !setup.data && (
        <div className="flex items-center gap-2 text-sm text-neutral-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Checking…
        </div>
      )}

      {setup.data && (
        <>
          <div className="flex flex-wrap items-center gap-3 text-xs text-neutral-500 dark:text-neutral-400">
            <span>
              {open === 0 ? 'Everything is in place.' : `${open} of ${items.length} items need attention.`}
            </span>
            {['ok', 'warn', 'missing', 'unknown'].map(
              (s) =>
                counts[s] > 0 && (
                  <span key={s} className={`rounded-full px-2 py-0.5 ${STATUS_STYLES[s]}`}>
                    {counts[s]} {STATUS_LABELS[s].toLowerCase()}
                  </span>
                )
            )}
            <span>
              · checked {fmt(setup.data.checkedAt)}
              {setup.data.live ? ' (live)' : ''} · {setup.data.tenants} tenant{setup.data.tenants === 1 ? '' : 's'}
            </span>
          </div>

          <ol className="divide-y divide-neutral-200 rounded-lg border border-neutral-200 bg-white dark:divide-neutral-700 dark:border-neutral-700 dark:bg-neutral-800">
            {items.map((item, idx) => (
              <li key={item.key} className="p-4">
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 w-5 text-right text-xs text-neutral-400">{idx + 1}.</span>
                  <Dot status={item.status} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-neutral-900 dark:text-white">{item.label}</span>
                      <span className={`rounded-full px-2 py-0.5 text-xs ${STATUS_STYLES[item.status] || STATUS_STYLES.unknown}`}>
                        {STATUS_LABELS[item.status] || item.status}
                      </span>
                    </div>
                    {item.detail && (
                      <p className="mt-1 whitespace-pre-wrap break-words text-sm text-neutral-600 dark:text-neutral-300">{item.detail}</p>
                    )}
                    {item.action && (
                      <div className="mt-2">
                        <div className="mb-1 text-xs font-medium uppercase tracking-wide text-neutral-400">What to do</div>
                        <CopyBlock value={item.action} />
                      </div>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </>
      )}
    </div>
  );
}
